import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  startTransition,
} from "react";
import { useAuth } from "./useAuth";
import { useToast } from "./use-toast";

const TenantContext = createContext();

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error("useTenant must be used within a TenantProvider");
  }
  return context;
};

// Tenant API client with fallback support
const tenantAPI = {
  // Fallback mode when backend services are not available
  fallbackMode: false,

  async request(endpoint, options = {}) {
    // If in fallback mode, return mock data
    if (this.fallbackMode) {
      return this.getMockData(endpoint, options.method);
    }

    try {
      const url = `/api/tenants${endpoint}`;
      const config = {
        headers: {
          "Content-Type": "application/json",
          ...options.headers,
        },
        ...options,
      };

      if (config.body && typeof config.body === "object") {
        config.body = JSON.stringify(config.body);
      }

      const response = await fetch(url, config);

      // If API is down, enable fallback mode
      if (response.status >= 500) {
        console.warn(
          "Backend services unavailable, switching to fallback mode",
        );
        this.fallbackMode = true;
        return this.getMockData(endpoint, options.method);
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || `HTTP ${response.status}`);
      }

      return data;
    } catch (error) {
      console.warn("API request failed, using fallback mode:", error.message);
      this.fallbackMode = true;
      return this.getMockData(endpoint, options.method);
    }
  },

  getMockData(endpoint, method = "GET") {
    // Provide mock tenant data when backend is unavailable
    if (endpoint === "/current") {
      return {
        id: "local-tenant",
        name: "Local Development",
        domain: "localhost",
        status: "active",
        plan: "development",
        settings: {},
        createdAt: new Date().toISOString(),
      };
    }

    if (endpoint === "" && method === "GET") {
      return [
        {
          id: "local-tenant",
          name: "Local Development",
          domain: "localhost",
          status: "active",
          plan: "development",
        },
      ];
    }

    if (method === "POST") {
      return {
        id: "new-tenant-" + Date.now(),
        status: "created",
        ...endpoint,
      };
    }

    return {};
  },

  async getCurrentTenant() {
    return this.request("/current");
  },

  async listTenants() {
    return this.request("");
  },

  async createTenant(tenantData) {
    return this.request("", {
      method: "POST",
      body: tenantData,
    });
  },

  async switchTenant(tenantId) {
    return this.request(`/${tenantId}/switch`, {
      method: "POST",
    });
  },
};

export const TenantProvider = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const [currentTenant, setCurrentTenant] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const { toast } = useToast();

  // Reset tenant state when user logs out
  useEffect(() => {
    if (!authLoading && !user) {
      // Use startTransition to avoid suspension during synchronous logout
      startTransition(() => {
        setCurrentTenant(null);
        setTenants([]);
        setLoading(false);
        setIsInitialized(true);
      });
    }
  }, [user, authLoading]);

  const fetchCurrentTenant = useCallback(async () => {
    try {
      setLoading(true);
      const response = await tenantAPI.getCurrentTenant();

      if (response.tenant) {
        setCurrentTenant(response.tenant);

        // If we don't have a tenants list, create a simple one
        if (tenants.length === 0) {
          setTenants([response.tenant]);
        }
      } else {
        // No tenant found, create a default one
        await handleNoTenants();
      }
    } catch (error) {
      console.error("Error fetching current tenant:", error);

      // Create a fallback tenant for the current domain
      const fallbackTenant = {
        id: "main",
        name: "Wixell",
        subdomain: "app",
        role: "admin",
        settings: {
          theme: "professional",
          features: ["invoicing", "customers", "analytics"],
        },
      };

      setCurrentTenant(fallbackTenant);
      setTenants([fallbackTenant]);

      toast({
        title: "Using Default Tenant",
        description: "Connected to main Wixell tenant",
      });
    } finally {
      setLoading(false);
      setIsInitialized(true);
    }
  }, [tenants.length, toast]);

  const handleNoTenants = async () => {
    try {
      // Try to create a default tenant
      const defaultTenantData = {
        name: user?.name || "My Organization",
        subdomain: "default",
        settings: {
          theme: "professional",
          features: ["invoicing", "customers"],
        },
      };

      const response = await tenantAPI.createTenant(defaultTenantData);

      if (response.success && response.tenant) {
        setCurrentTenant(response.tenant);
        setTenants([response.tenant]);

        toast({
          title: "Welcome!",
          description: "Your organization has been set up successfully",
        });
      }
    } catch (error) {
      console.error("Error creating default tenant:", error);

      // Create a basic fallback tenant
      const fallbackTenant = {
        id: "default",
        name: "Default Organization",
        subdomain: "default",
        role: "admin",
        settings: {
          theme: "professional",
          features: ["invoicing", "customers"],
        },
      };

      setCurrentTenant(fallbackTenant);
      setTenants([fallbackTenant]);
    }
  };

  const fetchTenants = useCallback(async () => {
    try {
      const response = await tenantAPI.listTenants();

      if (response.tenants && Array.isArray(response.tenants)) {
        setTenants(response.tenants);

        // If no current tenant is set, use the first one
        if (!currentTenant && response.tenants.length > 0) {
          setCurrentTenant(response.tenants[0]);
        }
      }
    } catch (error) {
      console.error("Error fetching tenants:", error);
      // Don't show error to user as this is not critical
    }
  }, [currentTenant]);

  useEffect(() => {
    // Only fetch tenant data when user is authenticated and not yet initialized
    if (!authLoading && user && !isInitialized) {
      fetchCurrentTenant();
    }
  }, [user, authLoading, isInitialized]);

  const switchTenant = async (tenantId) => {
    try {
      const tenant = tenants.find((t) => t.id === tenantId);
      if (!tenant) {
        toast({
          title: "Error",
          description: "Tenant not found",
          variant: "destructive",
        });
        return;
      }

      // Try to switch via API first
      try {
        await tenantAPI.switchTenant(tenantId);
      } catch (error) {
        console.warn("API tenant switch failed, using local switch:", error);
      }

      // Update local state
      setCurrentTenant(tenant);

      toast({
        title: "Organization switched",
        description: `Now working in ${tenant.name}`,
      });

      // Reload the page to ensure all components refresh with new tenant
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      console.error("Error switching tenant:", error);
      toast({
        title: "Error",
        description: "Failed to switch organization",
        variant: "destructive",
      });
    }
  };

  const refreshTenants = () => {
    fetchCurrentTenant();
    fetchTenants();
  };

  const createTenant = async (tenantData) => {
    try {
      setLoading(true);

      const response = await tenantAPI.createTenant({
        name: tenantData.name,
        subdomain:
          tenantData.subdomain ||
          tenantData.name.toLowerCase().replace(/\s+/g, "-"),
        settings: tenantData.settings || {
          theme: "professional",
          features: ["invoicing", "customers"],
        },
      });

      if (response.success && response.tenant) {
        // Add to local tenants list
        const newTenantsList = [...tenants, response.tenant];
        setTenants(newTenantsList);

        toast({
          title: "Success",
          description: `Organization "${tenantData.name}" created successfully`,
        });

        return response.tenant;
      } else {
        throw new Error(response.error || "Failed to create tenant");
      }
    } catch (error) {
      console.error("Error creating tenant:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to create organization",
        variant: "destructive",
      });
      return null;
    } finally {
      setLoading(false);
    }
  };

  const getTenantSettings = (key, defaultValue = null) => {
    return currentTenant?.settings?.[key] || defaultValue;
  };

  const updateTenantSettings = async (settings) => {
    if (!currentTenant) return;

    try {
      const updatedTenant = {
        ...currentTenant,
        settings: {
          ...currentTenant.settings,
          ...settings,
        },
      };

      setCurrentTenant(updatedTenant);

      // Update in tenants list
      setTenants((prev) =>
        prev.map((t) => (t.id === currentTenant.id ? updatedTenant : t)),
      );

      toast({
        title: "Settings Updated",
        description: "Your organization settings have been saved",
      });

      return updatedTenant;
    } catch (error) {
      console.error("Error updating tenant settings:", error);
      toast({
        title: "Error",
        description: "Failed to update settings",
        variant: "destructive",
      });
      return null;
    }
  };

  const value = {
    currentTenant,
    tenants,
    switchTenant,
    loading,
    refreshTenants,
    createTenant,
    getTenantSettings,
    updateTenantSettings,
    // Computed properties for convenience
    tenantId: currentTenant?.id,
    tenantName: currentTenant?.name,
    tenantRole: currentTenant?.role || "user",
    isAdmin: currentTenant?.role === "admin",
  };

  // Don't render children until auth is loaded to prevent Suspense errors
  if (authLoading) {
    return null;
  }

  return (
    <TenantContext.Provider value={value}>{children}</TenantContext.Provider>
  );
};
